import { font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Subsection } from './Subsection';
import type { SubsectionProps } from './Subsection';

const SectionContainer = styled.section<{
  rows: number;
  intersecting: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;

  & h2,
  & h2 a {
    ${font('main', {
      fontSize: '2.4rem',
      fontWeight: 700,
      lineHeight: '2.8rem',
    })}
    color: ${themedColor('night')};
  }

  & header {
    position: sticky;
    top: 7rem;
    background: #ffffff;
  }

  & h2 {
    padding: 1.6rem 20px;
    max-width: 120rem;
    margin: 0 auto;
    width: 100%;
    border-bottom: ${({ intersecting }) => (!intersecting ? css`1px solid ${themedColor('glass')}` : 'none')};
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(${(props: { rows: number }) => props.rows}, 1fr);

        & header {
            position: static;
            grid-row: 1 / -1;
            border-bottom: none;
        }
    `}
`;

const ObserverContainer = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid ${themedColor('glass')};
  }
`;

export interface SectionProps {
  title: string;
  href: string;
  subsections: SubsectionProps[];
}

export const Section: React.FC<SectionProps> = ({ title, subsections, href }) => {
  const container = useRef(null);
  const [intersecting, setIntersecting] = useState(false);
  const Text = href ? 'a' : 'span';

  useEffect(() => {
    if (!container.current) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIntersecting(entry.isIntersecting);
        });
      },
      {
        rootMargin: '-70px 0px 0px 0px',
        threshold: 1,
      },
    );
    observer.observe(container.current);

    return () => observer.disconnect();
  }, []);
  return (
    <ObserverContainer>
      <span data-observer ref={container} />
      <SectionContainer
        intersecting={intersecting}
        rows={Math.ceil(subsections.length / 2)}
        aria-labelledby={`title-${title}`}
      >
        <header>
          <h2 id={`title-${title}`}>
            <Text href={href || undefined}>{title}</Text>
          </h2>
        </header>
        {subsections.map(ss => (
          <Subsection key={ss.title} title={ss.title} items={ss.items} href={ss.href} />
        ))}
      </SectionContainer>
    </ObserverContainer>
  );
};
